import React from "react";

type StatusKey =
  | "New"
  | "Scheduled"
  | "Escalated"
  | "Pending"
  | "In-Progress"
  | "Canceled"
  | "Email Ticket"
  | "Blocked";

const STATUS_OPTIONS: Array<{
  key: StatusKey;
  label: string;
  color: string;
  default?: boolean;
}> = [
  { key: "New", label: "New", color: "#2e7d32" },
  { key: "Scheduled", label: "Scheduled", color: "#6a1b9a" },
  { key: "Escalated", label: "Escalated", color: "#c62828" },
  { key: "Pending", label: "Pending", color: "#b26a00" },
  { key: "In-Progress", label: "In-Progress", color: "#1565c0" },
  { key: "Canceled", label: "Canceled", color: "#8d6e63" },
  { key: "Email Ticket", label: "Email Ticket", color: "#37474f" },
  { key: "Blocked", label: "Blocked", color: "#ef6c00" },
];

const COMPLETED_STATUS = "Completed" as const;

export function StatusFilters({
  counts,
  onChange,
  className,
}: {
  counts?: Partial<Record<StatusKey, number>>;
  onChange?: (selected: string[]) => void;
  className?: string;
}) {
  const defaultSelected = STATUS_OPTIONS.filter(s => s.default).map(s => s.key);

  const [selected, setSelected] = React.useState<string[]>(defaultSelected);
  const [isCompletedView, setIsCompletedView] = React.useState(false);

  // store previous statuses so Back works
  const previousSelectedRef = React.useRef<string[]>(defaultSelected);

  React.useEffect(() => {
    onChange?.(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key: StatusKey) => {
    setSelected(prev => {
      const next = prev.includes(key)
        ? prev.filter(x => x !== key)
        : [...prev, key];

      previousSelectedRef.current = next;
      onChange?.(next);
      return next;
    });
  };

  const enterCompleted = () => {
    previousSelectedRef.current = selected;
    setIsCompletedView(true);
    setSelected([COMPLETED_STATUS]);
    onChange?.([COMPLETED_STATUS]);
  };

  const exitCompleted = () => {
    setIsCompletedView(false);
    setSelected(previousSelectedRef.current);
    onChange?.(previousSelectedRef.current);
  };

  return (
    <div className={`status-toolbar ${className ?? ""}`}>
      <style>
{`
  .status-toolbar { width: 100%; }

  /* Key: left takes remaining space, right is pinned */
  .status-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .status-left {
    flex: 1 1 auto;           /* takes available space */
    min-width: 0;             /* allows wrapping properly */
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .status-right {
    flex: 0 0 auto;           /* fixed size */
    margin-left: auto;        /* push to far right */
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* SMALL pill base (use !important to beat Bootstrap) */
  .status-toolbar .status-pill {
    --status-color: #0d6efd;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    padding: 6px 12px !important;
    min-height: 30px !important;
    border-radius: 10px !important;

    border: 2px solid var(--status-color) !important;
    background: transparent !important;

    color: var(--status-color) !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    line-height: 1 !important;

    cursor: pointer !important;
    user-select: none !important;
    white-space: nowrap !important;

    transition: background-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
  }

  .status-toolbar .status-pill:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0,0,0,0.12);
  }

  .status-toolbar .btn-check:checked + .status-pill {
    background: var(--status-color) !important;
    color: #fff !important;
  }

  /* Special buttons */
  .completed-pill { --status-color: #0f172a; }
  .back-pill { --status-color: #64748b; }

  /* Mobile: keep right aligned, allow wrapping */
  @media (max-width: 768px) {
    .status-row { flex-wrap: wrap; }
    .status-right { margin-left: 0; width: 100%; justify-content: flex-end; }
  }
`}
</style>

<div className="status-row">
  {/* LEFT: normal statuses */}
  {!isCompletedView && (
    <div className="status-left" role="group" aria-label="Ticket status filters">
      {STATUS_OPTIONS.map((s) => {
        const id = `status-${s.key.replace(/\s+/g, "").toLowerCase()}`;
        const isOn = selected.includes(s.key);

        return (
          <React.Fragment key={s.key}>
            <input
              type="checkbox"
              className="btn-check"
              id={id}
              autoComplete="off"
              checked={isOn}
              onChange={() => toggle(s.key)}
            />
            <label
              htmlFor={id}
              className="status-pill"
              style={{ ["--status-color" as any]: s.color }}
            >
              {s.label}
            </label>
          </React.Fragment>
        );
      })}
    </div>
  )}

  {/* RIGHT: Completed / Back pinned to far right */}
  <div className="status-right">
    {!isCompletedView ? (
      <button
        type="button"
        className="status-pill completed-pill"
        onClick={enterCompleted}
      >
        Completed
      </button>
    ) : (
      <button
        type="button"
        className="status-pill back-pill"
        onClick={exitCompleted}
      >
        ← Back
      </button>
    )}
  </div>
</div>
    </div>
  );
}